// src/pages/TrunksPage.js
import React, { useEffect, useState, useContext } from 'react';
import {
  Box, Typography, Card, CardContent, Button, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Paper, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField,
  IconButton, Tooltip, Grid, Divider, MenuItem,
  FormGroup, FormControlLabel, Checkbox, FormLabel
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, SettingsEthernet as SettingsEthernetIcon, ArrowUpward as ArrowUpwardIcon, ArrowDownward as ArrowDownwardIcon } from '@mui/icons-material';
import { Controller, useForm } from 'react-hook-form';
import { ApiContext } from '../contexts/ApiContext';
import { useNotification } from '../contexts/NotificationContext';
import { usePermissions } from '../hooks/usePermissions';

const defaultValues = {
  idtrunk: undefined,
  nombre: '',
  type: 'SIP',
  parametros:
    'type=peer\n' +
    'dtmfmode=rfc2833\n' +
    'qualify=yes\n' +
    'nat=force_rport,comedia\n' +
    'insecure=invite,port\n' +
    'port=5060\n' +
    'host=\n' +
    'directmedia=off\n' +
    'context=pbxmasterin\n',
  ulaw: true,
  alaw: true,
  gsm: false,
  g729: true,
  opus: false,
  codecOrder: ['g729', 'ulaw', 'alaw', 'opus', 'gsm'],
  activo: true,
  channels: 0,
  prefix: '',
  registerConfig: { register: false, username: '', password: '', server: '' },
};

export default function TrunksPage() {
  const { trunks } = useContext(ApiContext);
  const { showSuccess, showError } = useNotification();
  const { canAction } = usePermissions();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editing, setEditing] = useState(null);

  const { control, handleSubmit, reset, watch, setValue, formState: { errors } } = useForm({
    defaultValues,
    shouldUnregister: false,
  });
  const registerOn = watch('registerConfig.register');
  const currentParams = watch('parametros');
  const codecOrder = watch('codecOrder');
  const ulaw = watch('ulaw');
  const alaw = watch('alaw');
  const gsm = watch('gsm');
  const g729 = watch('g729');
  const opus = watch('opus');

  useEffect(() => { loadData(); }, []);

  const defaultCodecOrder = defaultValues.codecOrder;

  const parseCodecOrderFromParams = (parametros) => {
    const order = [];
    String(parametros || '')
      .split(/\r?\n/)
      .map((l) => l.trim().toLowerCase())
      .forEach((l) => {
        const m = l.match(/^allow\s*=\s*(\w+)/i);
        if (m && m[1]) order.push(m[1]);
      });
    return order;
  };

  const mergeCodecOrder = (parsedOrder) => {
    const merged = [];
    parsedOrder.forEach((c) => {
      if (!merged.includes(c)) merged.push(c);
    });
    defaultCodecOrder.forEach((c) => {
      if (!merged.includes(c)) merged.push(c);
    });
    return merged;
  };

  const stripCodecLines = (parametros) =>
    String(parametros || '')
      .split(/\r?\n/)
      .map((l) => l.trim())
      .filter(Boolean)
      .filter((l) => !/^allow\s*=|^disallow\s*=/i.test(l));

  const buildParametros = (parametros, order, flags) => {
    const lines = stripCodecLines(parametros);
    const allows = (order || [])
      .filter((c) => flags?.[c])
      .map((c) => `allow=${c}`);
    return [...lines, 'disallow=all', ...allows].join('\n');
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const res = await trunks.getAll();
      // Soporta ambos formatos: array directo o envuelto en {data:{trunks:[]}}
      const list =
        (res && res.data && (res.data.trunks || res.data)) ||
        res ||
        [];
      setRows(Array.isArray(list) ? list : []);
    } catch (err) {
      showError('Error al cargar troncales');
      console.error('Load trunks error:', err);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };


  const handleOpenDialog = (row = null) => {
    setEditing(row);
    if (row) {
      let registerConfig = { register: false, username: '', password: '', server: '' };
      try {
        if (row.config) {
          const cfg = JSON.parse(row.config);
          if (cfg.register) registerConfig = cfg.register;
        }
      } catch {}
      const parsedOrder = parseCodecOrderFromParams(row.parametros || '');
      const hasParsed = new Set(parsedOrder);
      const codecOrderFromParams = parsedOrder.length ? mergeCodecOrder(parsedOrder) : defaultCodecOrder;
      reset({
        ...defaultValues,
        ...row,
        ulaw: !!row.ulaw || hasParsed.has('ulaw'),
        alaw: !!row.alaw || hasParsed.has('alaw'),
        gsm: !!row.gsm || hasParsed.has('gsm'),
        g729: !!row.g729 || hasParsed.has('g729'),
        opus: hasParsed.has('opus'),
        codecOrder: codecOrderFromParams,
        activo: !!row.activo,
        registerConfig,
      });
    } else {
      reset(defaultValues);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditing(null);
  };

  useEffect(() => {
    if (!openDialog) return;
    const next = buildParametros(currentParams, codecOrder, { ulaw, alaw, gsm, g729, opus });
    if (next !== currentParams) {
      setValue('parametros', next, { shouldDirty: true, shouldTouch: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openDialog, codecOrder, ulaw, alaw, gsm, g729, opus, setValue]);

  const onSubmit = async (values) => {
    try {
      const parametrosFinal = buildParametros(values.parametros, values.codecOrder, values);
      const payload = {
        ...values,
        ulaw: values.ulaw ? 1 : 0,
        alaw: values.alaw ? 1 : 0,
        gsm: values.gsm ? 1 : 0,
        g729: values.g729 ? 1 : 0,
        activo: values.activo ? 1 : 0,
        parametros: parametrosFinal,
      };

      let resp;
      if (values.idtrunk) {
        resp = await trunks.update(values.idtrunk, payload);
      } else {
        resp = await trunks.create(payload);
      }

      // Mensaje de éxito + sincronización
      const synced = resp?.deploy?.ok ? '' : '';
      showSuccess(`Troncal ${values.idtrunk ? 'actualizada' : 'creada'} correctamente${synced}`);

      handleCloseDialog();
      loadData();
    } catch (err) {
      const msg = err?.message || 'Error al guardar/sincronizar troncal';
      showError(msg);
      console.error('Save trunk error:', err);
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`¿Eliminar la troncal "${row.nombre}"?`)) return;
    try {
      const resp = await trunks.remove(row.idtrunk);
      const synced = resp?.deploy?.ok ? '' : '';
      showSuccess(`Troncal eliminada correctamente${synced}`);
      loadData();
    } catch (err) {
      const msg = err?.message || 'Error al eliminar/sincronizar troncal';
      showError(msg);
      console.error('Delete trunk error:', err);
    }
  };

  const codecBadges = (r) => {
    const cs = [];
    if (r.ulaw) cs.push('ulaw');
    if (r.alaw) cs.push('alaw');
    if (r.gsm)  cs.push('gsm');
    if (r.g729) cs.push('g729');
    if (r.parametros && /allow\s*=\s*opus/i.test(r.parametros)) cs.push('opus');
    return cs.join(', ');
  };

  const moveCodec = (codec, dir) => {
    const list = Array.isArray(codecOrder) ? [...codecOrder] : [...defaultCodecOrder];
    const idx = list.indexOf(codec);
    if (idx === -1) return;
    const nextIdx = dir === 'up' ? idx - 1 : idx + 1;
    if (nextIdx < 0 || nextIdx >= list.length) return;
    [list[idx], list[nextIdx]] = [list[nextIdx], list[idx]];
    setValue('codecOrder', list, { shouldDirty: true });
  };

  return (
    <Box p={2}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="h5" fontWeight={600}>Troncales</Typography>
        {canAction('create_trunk') && (
          <Button startIcon={<AddIcon />} variant="contained" onClick={() => handleOpenDialog(null)}>
            Nueva Troncal
          </Button>
        )}
      </Box>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" gap={3} alignItems="center">
            <SettingsEthernetIcon fontSize="small" />
            <Typography variant="body2">Total: {rows.length}</Typography>
          </Box>
        </CardContent>
      </Card>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Nombre</TableCell>
              <TableCell>Tipo</TableCell>
              <TableCell>Codecs</TableCell>
              <TableCell>Canales</TableCell>
              <TableCell>Prefijo</TableCell>
              <TableCell>Activo</TableCell>
              <TableCell align="right">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.idtrunk} hover>
                <TableCell>{r.nombre}</TableCell>
                <TableCell>{r.type || 'SIP'}</TableCell>
                <TableCell>{codecBadges(r)}</TableCell>
                <TableCell>{r.channels ?? 0}</TableCell>
                <TableCell>{r.prefix ?? '-'}</TableCell>
                <TableCell>{r.activo ? 'sí' : 'no'}</TableCell>
                <TableCell align="right">
                  {canAction('edit_trunk') && (
                    <Tooltip title="Editar">
                      <IconButton size="small" onClick={() => handleOpenDialog(r)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {canAction('delete_trunk') && (
                    <Tooltip title="Eliminar">
                      <IconButton size="small" onClick={() => handleDelete(r)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && !loading && (
              <TableRow><TableCell colSpan={7} align="center">No hay troncales.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialogo Crear/Editar */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{editing ? 'Editar Troncal' : 'Nueva Troncal'}</DialogTitle>
        <DialogContent dividers>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Controller
                name="nombre"
                control={control}
                rules={{ required: 'Requerido' }}
                render={({ field }) => (
                  <TextField {...field} label="Nombre" fullWidth error={!!errors.nombre} helperText={errors.nombre?.message} />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Controller
                name="type"
                control={control}
                render={({ field }) => (
                  <TextField {...field} label="Tipo" select fullWidth>
                    <MenuItem value="SIP">SIP</MenuItem>
                  </TextField>
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider textAlign="left">Registro (opcional)</Divider>
              <FormGroup row sx={{ mt: 1 }}>
                <FormControlLabel
                  control={
                    <Controller
                      name="registerConfig.register"
                      control={control}
                      render={({ field }) => <Checkbox {...field} checked={!!field.value} />}
                    />
                  }
                  label="Register"
                />
              </FormGroup>
            </Grid>

            <Grid item xs={12} md={4}>
              <Controller
                name="registerConfig.username"
                control={control}
                render={({ field }) => (
                  <TextField {...field} label="Username" fullWidth disabled={!registerOn} />
                )}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Controller
                name="registerConfig.password"
                control={control}
                render={({ field }) => (
                  <TextField {...field} label="Password" type="password" fullWidth disabled={!registerOn} />
                )}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Controller
                name="registerConfig.server"
                control={control}
                render={({ field }) => (
                  <TextField {...field} label="Server (host:5060)" fullWidth disabled={!registerOn} />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider textAlign="left">Definición</Divider>
              <Controller
                name="parametros"
                control={control}
                render={({ field }) => (
                  <TextField {...field} label="Parámetros (peer)" fullWidth multiline minRows={8} />
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormLabel component="legend">Codecs (orden y habilitar)</FormLabel>
              <Box display="flex" flexDirection="column" gap={1} mt={1}>
                {(codecOrder || defaultCodecOrder).map((c, idx, arr) => (
                  <Box key={c} display="flex" alignItems="center" justifyContent="space-between">
                    <FormControlLabel
                      control={
                        <Controller
                          name={c}
                          control={control}
                          render={({ field }) => <Checkbox {...field} checked={!!field.value} />}
                        />
                      }
                      label={c}
                    />
                    <Box>
                      <IconButton size="small" onClick={() => moveCodec(c, 'up')} disabled={idx === 0}>
                        <ArrowUpwardIcon fontSize="inherit" />
                      </IconButton>
                      <IconButton size="small" onClick={() => moveCodec(c, 'down')} disabled={idx === arr.length - 1}>
                        <ArrowDownwardIcon fontSize="inherit" />
                      </IconButton>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormLabel component="legend">Otros</FormLabel>
              <FormGroup>
                <Controller
                  name="channels"
                  control={control}
                  render={({ field }) => <TextField {...field} type="number" label="Número de canales" fullWidth />}
                />
                <Controller
                  name="prefix"
                  control={control}
                  render={({ field }) => <TextField {...field} label="Prefijo" fullWidth />}
                />
                <FormControlLabel control={
                  <Controller name="activo" control={control} render={({ field }) => <Checkbox {...field} checked={!!field.value} />} />
                } label="Activo" />
              </FormGroup>
            </Grid>
          </Grid>

        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button variant="contained" onClick={handleSubmit(onSubmit)}>
            {editing ? 'Guardar cambios' : 'Crear'}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
}
